import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { answerRefAddress, useAnswerRefInfo, useAnswerRefScope } from './answer-ref-context';

import { CommitField } from '@/components/ui/commit-field';
import { POPUP_BACKDROP_LEVEL, POPUP_LEVEL } from '@/components/ui/popup-level';

/**
 * NAMING A RECALLED ANSWER FROM INSIDE AN EQUATION.
 *
 * A reference's default name is its address — "Solve - Landing Location · Landing Location" —
 * and an equation is where that costs the most: the chip is most of the line, and the line is
 * the thing the teacher is reading to check their own arithmetic. So this is the surface where
 * a short name is worth the most, and it was the one surface with no way to set one.
 *
 * ## Only the name
 *
 * The prose chip's popup also carries the digit rows. They are deliberately absent here: a
 * recall in an equation is a VALUE, computed with, not text being read. A digit setting there
 * could only mean one of two things and both are wrong — round what the grader uses, which
 * changes the answer key invisibly, or do nothing at all, which puts a dead control in the one
 * place a teacher would most expect it to bite. Keeping digits to prose is also what lets
 * `recall-agreement`'s invariant stand unweakened: rounding applies where a number is READ,
 * never where it is computed with.
 *
 * ## Why not `SelectMenu`
 *
 * That is the shared menu and it would be the right answer if there were anything to choose.
 * There is not — this is one field — and its trigger and popup are one unit, while the thing
 * this anchors to is a span built by an html-string transform with no React to hang a trigger
 * on. A portalled box positioned from a rect is what the app already does for exactly that.
 */
export function RecallRenamePopover({ aliasId, anchor, onClose }: {
  aliasId: string;
  /** Where the chip was when it was clicked. Read once; see the scroll note below. */
  anchor: DOMRect;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const scope = useAnswerRefScope();
  const info = useAnswerRefInfo(aliasId);
  const boxRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: anchor.left, top: anchor.bottom + 4 });

  /* Measured once it has a width, which it does not have until it is in the document — the
     same reason `SelectMenu` fits itself in a layout effect rather than arithmetically. */
  useLayoutEffect(() => {
    const box = boxRef.current?.getBoundingClientRect();
    if (!box) return;
    const m = 8;
    setPos({
      left: Math.max(m, Math.min(anchor.left, window.innerWidth - box.width - m)),
      top: anchor.bottom + 4 + box.height > window.innerHeight
        ? Math.max(m, anchor.top - box.height - 4)
        : anchor.bottom + 4,
    });
    boxRef.current?.querySelector('input')?.focus();
  }, [anchor]);

  useEffect(() => {
    /*
     * A SCROLL CLOSES THIS ONE, where the shared menu now follows its trigger instead.
     *
     * The difference is what there is to follow: `SelectMenu` has a trigger element it can
     * re-measure, and this anchors to a rect taken from a span inside a contenteditable that
     * reflows as the equation is edited. There is nothing stable to track, and a field
     * floating beside the wrong token is worse than one that asked to be reopened.
     */
    const shut = () => onClose();
    window.addEventListener('scroll', shut, true);
    window.addEventListener('resize', shut);
    return () => {
      window.removeEventListener('scroll', shut, true);
      window.removeEventListener('resize', shut);
    };
  }, [onClose]);

  if (!scope?.updateAlias || !info) return null;

  const first = scope.aliases.find(a => a.id === aliasId)?.members[0];

  return createPortal(
    <>
      {/* Swallows the press that dismisses, so the editor underneath does not also act on it. */}
      <div
        data-noedit
        aria-hidden='true'
        className={`fixed inset-0 ${POPUP_BACKDROP_LEVEL}`}
        onMouseDown={e => { e.preventDefault(); onClose(); }}
      />
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- not a
          control: it catches Escape BUBBLING from the real field inside it, the same
          arrangement `select-menu` settled on */}
      <div
        ref={boxRef}
        role='group'
        aria-label={t('blocks.answerRef.rename.title')}
        style={{ left: pos.left, top: pos.top }}
        className={`fixed ${POPUP_LEVEL} w-64 max-w-[calc(100vw-16px)] rounded-lg border border-ws-border bg-ws-elevated p-2 shadow-lg`}
        onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}
      >
        <div className='flex flex-col gap-1'>
          {/*
            * THE STORED NAME, with no facet suffix — see `AnswerRefInfo.rawName`. `info.name`
            * is what the CHIP reads, family name and "(full)" together, and pre-filling the
            * field with that would store the suffix on the next commit.
            */}
          <CommitField
            value={info.rawName}
            placeholder={info.derived}
            /* The manager's own label for the same field, which this IS — a second string
               saying the same thing is how two surfaces come to call one control two names. */
            ariaLabel={t('blocks.answerRef.manager.nameLabel', { name: info.name })}
            onCommit={next => scope.updateAlias!(aliasId, {
              name: next.trim() === '' || next.trim() === info.derived ? null : next.trim(),
            })}
          />
          {/* WHICH ANSWER THIS IS, always — the chip in an equation shows a short name and the
              address is the only thing that says what it stands for. Wraps, because an address
              is a task title and a block name, both of them sentences the teacher wrote. */}
          {first && (
            <span className='break-words text-xs text-ws-muted'>
              {answerRefAddress(scope, first, t)}
            </span>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
